import { compact, flatten, get, has, merge, pick, fromPairs, intersection, uniq } from 'lodash-es'
import { parseISO, compareDesc } from 'date-fns/esm'

import {
  defaultSortKey as defaultFavoritesSortKey,
  sortKeys as favoritesSortKeys,
} from '@/js/components/Favorites/favorites-constants.js'
import { sortItemsBySortKey as favoritesSortItemsBySortKey } from '@/js/helper/favorites-helper.js'

import { REQUEST_STATE } from '@/js/plugins/store/modules/aimeos/constants'

import AIMEOS_META_CONSTANTS from '@/js/helper/aimeos-meta-constants.js'
import API_CONSTANTS from '@/js/helper/api-constants.js'
import GETTER_TYPES from '../getter-types'
import CLIENT_CONSTANTS from '@/js/helper/client-constants.js'
import LIST_CONSTANTS from '@/js/helper/aimeos-list-constants.js'
// import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'
// import PRODUCT_GETTERS from '@/js/plugins/store/modules/products/getter-types.js'
import CONSTANTS from '../constants'

import CHECKOUT_GETTERS from './checkout-getters'
import { getCustomerGetters } from './customer-getters'
import { getBasketGetters } from './basket-getters'
import { getServiceGetters } from './service-getters'
import { getOrderGetters } from './order-getters'
import { getProductsGetters } from './products-getters'
import { getBlockingreasonGetters } from './blockingreason-getters'
import { getProductSparepartsGetters } from './products-spareparts-getters'
import { getCompareGetters } from './compare-getters'
import { getAvailabilityWatchGetters } from './availability-watch-getters'

export const getGetters = MUTATION_TYPES => ({
  ...getServiceGetters(MUTATION_TYPES),
  ...getBasketGetters(MUTATION_TYPES),
  ...CHECKOUT_GETTERS,
  ...getCustomerGetters(MUTATION_TYPES),
  ...getOrderGetters(MUTATION_TYPES),
  ...getProductsGetters(MUTATION_TYPES),
  ...getProductSparepartsGetters(MUTATION_TYPES),
  ...getCompareGetters(MUTATION_TYPES),
  ...getBlockingreasonGetters(MUTATION_TYPES),
  ...getAvailabilityWatchGetters(MUTATION_TYPES),
  // Options
  [GETTER_TYPES.OPTIONS_PROMISE]: state => get(state, MUTATION_TYPES.OPTIONS.promiseKey),
  [GETTER_TYPES.OPTIONS_DATA]: state => get(state, MUTATION_TYPES.OPTIONS.dataKey, ''),
  [GETTER_TYPES.OPTIONS_CONTENT_BASEURL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        API_CONSTANTS.AIMEOS.META.PATHS.CONTENT_BASEURL,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_ATTRIBUTE_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.ATTRIBUTE,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_BASKET_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.BASKET,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_BASKET_URL_PROMISE]: (state, getters) => async () => {
    await getters[GETTER_TYPES.OPTIONS_PROMISE]
    return get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.BASKET,
      ],
      '',
    )
  },
  [GETTER_TYPES.OPTIONS_CATALOG_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.CATALOG,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_CUSTOMER_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.CUSTOMER,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_LOCALE_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.LOCALE,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_ORDER_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.ORDER,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_PRODUCT_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.PRODUCT,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_SPAREPARTS_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.SPAREPARTS,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_SKUS_URL]: state =>
    get(
      state,
      [MUTATION_TYPES.OPTIONS.dataKey, API_CONSTANTS.META.TYPE, ...AIMEOS_META_CONSTANTS.PATHS.SKU],
      '',
    ),
  [GETTER_TYPES.OPTIONS_FAVORITE_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.CUSTOMER,
      ],
      '',
    ).replace(/customer/i, 'customer%2Frelationships'),
  [GETTER_TYPES.OPTIONS_SERVICE_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.SERVICE,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_STOCK_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.STOCK,
      ],
      '',
    ),
  [GETTER_TYPES.OPTIONS_SUPPLIER_URL]: state =>
    get(
      state,
      [
        MUTATION_TYPES.OPTIONS.dataKey,
        API_CONSTANTS.META.TYPE,
        ...AIMEOS_META_CONSTANTS.PATHS.SUPPLIER,
      ],
      '',
    ),
  [GETTER_TYPES.GET_OPTIONS_PROMISE_URL]: (state, getters) => path => {
    return new Promise((resolve, reject) => {
      const waitTime = getters[`${MUTATION_TYPES.OPTIONS.BASE}_${REQUEST_STATE.STATE_INITIAL}`]
        ? 250
        : 0
      setTimeout(() => {
        if (getters[`${MUTATION_TYPES.OPTIONS.BASE}_${REQUEST_STATE.STATE_INITIAL}`]) {
          reject(new Error(`AIMEOS::Could not get Url. OPTIONS Request not started`))
        }
        state[MUTATION_TYPES.OPTIONS.promiseKey].then(() => {
          if (!getters[`${MUTATION_TYPES.OPTIONS.BASE}_${REQUEST_STATE.STATE_SUCCESS}`]) {
            reject(new Error(`AIMEOS::Could not get Url. OPTIONS Request Failed`))
          }

          resolve(get(state, [MUTATION_TYPES.OPTIONS.dataKey, API_CONSTANTS.META.TYPE, ...path]))
        })
      }, waitTime)
    })
  },
  [GETTER_TYPES.META]: state =>
    get(state, [MUTATION_TYPES.BASKET.dataKey, API_CONSTANTS.META.TYPE], {}),
  [GETTER_TYPES.SEARCH_DATA]: state => get(state, MUTATION_TYPES.SEARCH.dataKey, {}),
  [GETTER_TYPES.SEARCH_ATTRIBUTE_COUNTS]: state =>
    get(state, [MUTATION_TYPES.SEARCH.dataKey, 'attributeCounts'], {}),
  [GETTER_TYPES.SEARCH_CATEGORY_COUNTS]: state =>
    get(state, [MUTATION_TYPES.SEARCH.dataKey, 'catalogCounts'], {}),
  [GETTER_TYPES.SEARCH_PENDING]: state =>
    get(state, MUTATION_TYPES.SEARCH.stateKey, REQUEST_STATE.STATE_FAILURE) ===
    REQUEST_STATE.STATE_PENDING,
  [GETTER_TYPES.CLIENT_ARRAY]: state => {
    const clientData = get(state, MUTATION_TYPES.CLIENT.dataKey)
    return clientData ? Object.values(clientData) : []
  },
  [GETTER_TYPES.CLIENT_INORMATION]: (state, getters) =>
    getters[GETTER_TYPES.CLIENT_ARRAY].map(client => ({
      [CLIENT_CONSTANTS.CLIENT]: get(client, CLIENT_CONSTANTS.PATHS.CLIENT),
      [CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_LONG]: get(
        client,
        CLIENT_CONSTANTS.PATHS.INFORMATION_PAYMENT_CONDITION_LONG,
      ),
      [CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_SHORT]: get(
        client,
        CLIENT_CONSTANTS.PATHS.INFORMATION_PAYMENT_CONDITION_SHORT,
      ),
      [CLIENT_CONSTANTS.INFORMATION_FREIGHT]: get(
        client,
        CLIENT_CONSTANTS.PATHS.INFORMATION_FREIGHT,
      ),
    })),
  [GETTER_TYPES.CLIENT_INORMATION_UNIQUE]: (state, getters) => {
    return getters[GETTER_TYPES.CLIENT_INORMATION].reduce(
      (uniquePaymentConditions, client) => {
        if (
          !has(uniquePaymentConditions, [
            CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_SHORT,
            client.payment_condition_short,
          ])
        ) {
          uniquePaymentConditions[CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_SHORT][
            client[CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_SHORT]
          ] = []
        }
        uniquePaymentConditions[CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_SHORT][
          client[CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_SHORT]
        ].push(client[CLIENT_CONSTANTS.CLIENT])

        if (
          !has(uniquePaymentConditions, [
            CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_LONG,
            client[CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_LONG],
          ])
        ) {
          uniquePaymentConditions[CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_LONG][
            client[CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_LONG]
          ] = []
        }
        uniquePaymentConditions[CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_LONG][
          client[CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_LONG]
        ].push(client[CLIENT_CONSTANTS.CLIENT])

        if (
          !has(uniquePaymentConditions, [
            CLIENT_CONSTANTS.INFORMATION_FREIGHT,
            client[CLIENT_CONSTANTS.INFORMATION_FREIGHT],
          ])
        ) {
          uniquePaymentConditions[CLIENT_CONSTANTS.INFORMATION_FREIGHT][
            client[CLIENT_CONSTANTS.INFORMATION_FREIGHT]
          ] = []
        }
        uniquePaymentConditions[CLIENT_CONSTANTS.INFORMATION_FREIGHT][
          client[CLIENT_CONSTANTS.INFORMATION_FREIGHT]
        ].push(client[CLIENT_CONSTANTS.CLIENT])

        return uniquePaymentConditions
      },
      {
        [CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_LONG]: {},
        [CLIENT_CONSTANTS.INFORMATION_PAYMENT_CONDITION_SHORT]: {},
        [CLIENT_CONSTANTS.INFORMATION_FREIGHT]: {},
      },
    )
  },
  [GETTER_TYPES.CLIENT_INORMATION_UNIQUE_CHECKED]: (state, getters) => {
    return Object.entries(getters[GETTER_TYPES.CLIENT_INORMATION_UNIQUE]).reduce(
      (checked, [info, conditions]) => {
        const conditionStrings = Object.keys(conditions)
        if (!conditionStrings.length) {
          return checked
        }
        const count = conditionStrings.length
        checked[info] =
          count > 1 ? conditions : { [conditionStrings[0]]: [CLIENT_CONSTANTS.ALL_CLIENTS] }
        return checked
      },
      {},
    )
  },
  [GETTER_TYPES.FAVORITE_PENDING]: state =>
    // [REQUEST_STATE.STATE_INITIAL, REQUEST_STATE.STATE_PENDING].includes(
    //   get(state, MUTATION_TYPES.FAVORITE.stateKey, REQUEST_STATE.STATE_FAILURE),
    // ),
    get(state, MUTATION_TYPES.FAVORITE.stateKey, REQUEST_STATE.STATE_FAILURE) ===
    REQUEST_STATE.STATE_PENDING,
  [GETTER_TYPES.FAVORITE_SKU_TO_RELATEDID]: (state, getters) => {
    return getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_CONFIG_OBJECT].reduce((acc, item) => {
      const favItemCode = get(item, ['favoriteConfig', 'code'])
      acc[favItemCode] = item.id
      return acc
    }, {})
  },
  // [GETTER_TYPES.FAVORITE_PRODUCT_ID_TO_RELATEDID]: state => {
  //   return get(state, [MUTATION_TYPES.FAVORITE.dataKey, API_CONSTANTS.DATA], []).reduce(
  //     (acc, item) => {
  //       const refId = get(item, LIST_CONSTANTS.PATHS.CUSTOMER_LISTS_REFID)
  //       acc[refId] = item.id
  //       return acc
  //     },
  //     {},
  //   )
  // },
  // [GETTER_TYPES.FAVORITE_PRODUCT_ID_TO_SKU]: state => {
  //   return get(state, [MUTATION_TYPES.FAVORITE.dataKey, API_CONSTANTS.DATA], []).reduce(
  //     (acc, item) => {
  //       const refId = get(item, LIST_CONSTANTS.PATHS.CUSTOMER_LISTS_REFID)

  //       const favItemCode = get(
  //         get(item, LIST_CONSTANTS.PATHS.CUSTOMER_LISTS_CONFIG, []).find(
  //           config => config.type === 'code',
  //         ),
  //         'value',
  //       )
  //       const favItemType = get(
  //         get(item, LIST_CONSTANTS.PATHS.CUSTOMER_LISTS_CONFIG, []).find(
  //           config => config.type === 'type',
  //         ),
  //         'value',
  //       )
  //       const favItem =
  //         favItemCode && favItemType
  //           ? {
  //               code: favItemCode,
  //               type: favItemType,
  //             }
  //           : undefined

  //       const favParentCode = get(
  //         get(item, LIST_CONSTANTS.PATHS.CUSTOMER_LISTS_CONFIG, []).find(
  //           config => config.type === 'parent',
  //         ),
  //         'value.code',
  //       )
  //       const favParent = favParentCode
  //         ? {
  //             code: favParentCode,
  //             type: API_CONSTANTS.AIMEOS.PRODUCTS.TYPE.SELECT,
  //           }
  //         : undefined

  //       acc[refId] = compact([favItem, favParent])
  //       return acc
  //     },
  //     {},
  //   )
  // },
  [GETTER_TYPES.FAVORITE_COUNT]: (state, getters) =>
    getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_CONFIG_OBJECT].length,
  [GETTER_TYPES.FAVORITE_PRODUCT_AND_PARENTS_SKUS]: (state, getters) => {
    return uniq(
      compact(
        getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_CONFIG_OBJECT].reduce((acc, item) => {
          acc.push(get(item, ['favoriteConfig', 'code']))
          acc.push(get(item, ['favoriteConfig', 'parent', 'code']))
          return acc
        }, []),
      ),
    )
  },
  [GETTER_TYPES.FAVORITE_PRODUCT_SKUS]: (state, getters) => {
    return compact(Object.keys(getters[GETTER_TYPES.FAVORITE_SKU_TO_RELATEDID]))
  },
  [GETTER_TYPES.FAVORITE_VARIANT_SKUS]: (state, getters) => {
    return getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_CONFIG_OBJECT]
      .filter(
        item =>
          get(item, ['favoriteConfig', 'type']) === API_CONSTANTS.AIMEOS.PRODUCTS.TYPE.DEFAULT,
      )
      .map(item => get(item, ['favoriteConfig', 'code']))
    // return compact(
    //   flatten(Object.values(getters[GETTER_TYPES.FAVORITE_PRODUCT_ID_TO_SKU]))
    //     .filter(item => item.type == API_CONSTANTS.AIMEOS.PRODUCTS.TYPE.DEFAULT)
    //     .map(item => item.code),
    // )
  },
  [GETTER_TYPES.FAVORITE_SKU_AVAILABILITIES]:
    (state, getters /*, rootState, rootGetters*/) =>
    ({ availabilities }) => {
      return getters[GETTER_TYPES.FAVORITE_VARIANT_SKUS].map(sku => ({
        sku,
        // availability: rootGetters[`${STOREMODULES.PRODUCTS}/${PRODUCT_GETTERS.AVAILABILITY}`](sku),
        availability: get(availabilities, sku),
      }))
    },
  [GETTER_TYPES.FAVORITE_SKU_AVAILABILITY_GROUPS]:
    (state, getters) =>
    ({ availabilities }) => {
      return getters[GETTER_TYPES.FAVORITE_SKU_AVAILABILITIES]({ availabilities }).reduce(
        (availabilityGroups, sku) => {
          const firstAvailability = get(sku.availability, 0)
          if (!firstAvailability) {
            return availabilityGroups
          }
          const { status } = firstAvailability
          if (!has(availabilityGroups, status)) {
            availabilityGroups[status] = []
          }
          availabilityGroups[status].push(sku.sku)
          return availabilityGroups
        },
        {},
      )
    },
  [GETTER_TYPES.FAVORITE_FILTER_DATA]:
    (state, getters) =>
    ({ availabilities }) => {
      // const brand = uniq(compact(this.dataArray.map(select => select.brand)))
      const brand = getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_FAVORITE_DATA].data.reduce(
        (brandGroups, { brand, variants, code }) => {
          if (!has(brandGroups, brand)) {
            brandGroups[brand] = []
          }
          brandGroups[brand] = [...brandGroups[brand], code, ...variants.map(item => item.code)]
          return brandGroups
        },
        {},
      )

      const availability = fromPairs(
        getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_FAVORITE_DATA].data.reduce(
          (availability, { variants, code }) => {
            // Add select code to availability code array when one variant has this availability
            return availability.map(([key, value]) => {
              return [
                key,
                intersection(
                  value,
                  variants.map(variant => variant.code),
                ).length
                  ? [...value, code]
                  : [...value],
              ]
            })
          },
          Object.entries(
            getters[GETTER_TYPES.FAVORITE_SKU_AVAILABILITY_GROUPS]({ availabilities }),
          ),
        ),
      )

      return {
        brand,
        availability,
        sort: favoritesSortKeys,
      }
    },
  [GETTER_TYPES.FAVORITE_PRODUCT_URL]: (state, getters) => {
    // const urlFilterParts = getters[GETTER_TYPES.FAVORITE_PRODUCT_SKUS]
    //   .map(id => `ai%5Bfilter%5D%5B%3D%3D%5D%5Bproduct.id%5D%5B%5D=${id}`)
    //   .join('&')

    return getters[GETTER_TYPES.FAVORITE_PRODUCT_SKUS].length
      ? `${getters[GETTER_TYPES.OPTIONS_SKUS_URL]}${
          /\?/.test(getters[GETTER_TYPES.OPTIONS_SKUS_URL]) ? '&' : '?'
        }ai%5Binclude%5D=media%2Cprice%2Cproduct%2Fproperty%2Cattribute%2Ctext&ai%5Bscope%5D=archive&ai%5BadditionalScope%5D=true`
      : undefined
  },
  // [GETTER_TYPES.FAVORITE_PRODUCT_URL]: (state, getters) => {
  //   const urlFilterParts = getters[GETTER_TYPES.FAVORITE_PRODUCT_IDS]
  //     .map(id => `ai%5Bfilter%5D%5B%3D%3D%5D%5Bproduct.id%5D%5B%5D=${id}`)
  //     .join('&')

  //   return getters[GETTER_TYPES.FAVORITE_PRODUCT_IDS].length
  //     ? `${getters[GETTER_TYPES.OPTIONS_PRODUCT_URL]}${
  //         /\?/.test(getters[GETTER_TYPES.OPTIONS_PRODUCT_URL]) ? '&' : '?'
  //       }ai%5Binclude%5D=media%2Cprice%2Cproduct%2Fproperty&${urlFilterParts}`
  //     : undefined
  // },
  [GETTER_TYPES.FAVORITEPRODUCTS_PENDING]: state =>
    // [REQUEST_STATE.STATE_INITIAL, REQUEST_STATE.STATE_PENDING].includes(
    //   get(state, MUTATION_TYPES.FAVORITEPRODUCTS.stateKey, REQUEST_STATE.STATE_FAILURE),
    // ),
    get(state, MUTATION_TYPES.FAVORITEPRODUCTS.stateKey, REQUEST_STATE.STATE_FAILURE) ===
    REQUEST_STATE.STATE_PENDING,
  [GETTER_TYPES.FAVORITEPRODUCTS_FAILED]: state =>
    get(state, MUTATION_TYPES.FAVORITEPRODUCTS.stateKey, REQUEST_STATE.STATE_FAILURE) ===
    REQUEST_STATE.STATE_FAILURE,
  [GETTER_TYPES.FAVORITEPRODUCTS_DATA]: state =>
    get(state, MUTATION_TYPES.FAVORITEPRODUCTS.dataKey, {}),
  [GETTER_TYPES.FAVORITEPRODUCTS_COUNT]: state =>
    get(state, [MUTATION_TYPES.FAVORITEPRODUCTS.dataKey, API_CONSTANTS.DATA], []).length,
  [GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_CONFIG_OBJECT]: state => {
    return get(state, [MUTATION_TYPES.FAVORITE.dataKey, API_CONSTANTS.DATA], [])
      .map(item => {
        const favoriteConfig = get(item, LIST_CONSTANTS.PATHS.CUSTOMER_LISTS_CONFIG, []).reduce(
          (config, configItem) => ({ ...config, [configItem.type]: configItem.value }),
          {},
        )
        return {
          ...item,
          favoriteConfig,
        }
      })
      .filter(item => item.favoriteConfig?.code)
  },
  [GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_FAVORITE_DATA]: (state, getters) => {
    // .sort(
    //   (a, b) =>
    //     get(a, LIST_CONSTANTS.PATHS.CUSTOMER_LISTS_POSITION) -
    //     get(b, LIST_CONSTANTS.PATHS.CUSTOMER_LISTS_POSITION),
    // )
    const productsData = getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA]
      ? getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA]
      : {}
    const favProducts = get(productsData, API_CONSTANTS.DATA, [])

    const favorites = getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_CONFIG_OBJECT].map(item => {
      const favoriteConfig = get(item, 'favoriteConfig', {})
      const favoriteCode = get(favoriteConfig, 'code')
      const favProduct = favProducts.find(product => product.code === favoriteCode)
      console.log('🚀 ~ favorites ~ favoriteCode:', favoriteCode, favProduct)
      const productId = favProduct
        ? get(favProduct, 'id')
        : get(item, LIST_CONSTANTS.PATHS.CUSTOMER_LISTS_REFID, [])
      const favoriteParent = get(favoriteConfig, 'parent')
      const favoriteTitle = get(favoriteConfig, 'title')
      const productType = get(favoriteConfig, 'type')

      const favoriteMergeData = {
        [LIST_CONSTANTS.ATTRIBUTE.CUSTOMER_LISTS_ID]: get(item, [
          API_CONSTANTS.ATTRIBUTES,
          LIST_CONSTANTS.ATTRIBUTE.CUSTOMER_LISTS_ID,
        ]),
        favoriteAvailable: !!favProduct,
        favoriteOriginalComment: get(favoriteConfig, 'comment'),
        favoriteAdded: parseISO(get(favoriteConfig, 'added')),
        id: productId,
        productId,
        contentBaseurl: '',
        brand: '',
        favoriteTitle,
        name: favoriteTitle,
        favoriteParent,
        parentId: favProduct ? get(favProduct, 'parentId') : undefined,
        parentCode: favoriteParent
          ? get(favoriteParent, 'code')
          : favProduct
          ? get(favProduct, 'parentCode')
          : undefined,
        productType,
        type: productType,
        code: favoriteCode,
      }

      return merge({}, favoriteMergeData, favProduct || {})
    })

    const selects = favorites.reduce((selects, favorite) => {
      let fallbackProduct = { productId: 'unknownParent' }
      const { productType, parentCode, favoriteParent, favoriteAdded, firstPrice, brand } = favorite
      const isSelect = productType === 'select'
      const selectProduct = isSelect
        ? { ...favorite, isFavorite: true }
        : parentCode
        ? favProducts.find(product => product.code === parentCode)
        : undefined

      if (
        !favoriteParent &&
        !isSelect &&
        favorite.parentCode &&
        favorite.parentId &&
        favorite.parentName
      ) {
        const { parentCode, parentId, parentName } = favorite
        fallbackProduct = {
          code: parentCode,
          name: parentName,
          id: parentId,
          productId: parentId,
          productType,
          type: 'variant',
        }
      }

      const select = selectProduct
        ? { ...pick(favoriteParent, ['code', 'name']), ...selectProduct, favoriteAvailable: true }
        : favoriteParent
        ? {
            ...pick(favoriteParent, ['code', 'name']),
            productId: favoriteParent.parentId,
            id: favoriteParent.parentId,
            productType: 'select',
            type: 'product',
            contentBaseurl: getters[GETTER_TYPES.OPTIONS_CONTENT_BASEURL],
          }
        : fallbackProduct

      // Ensure the sort attributes are available on the select:
      // fristPrice (lowest price from the grouped items), brand, maxFavoriteAdded (highest date from the grouped items)

      const selectPrice = get(select, 'firstPrice')
      const selectBrand = get(select, 'brand')

      if (!has(selects, select.code)) {
        selects[select.code] = {
          ...select,
          maxFavoriteAdded: favoriteAdded,
          firstPrice: !selectPrice || firstPrice < selectPrice ? firstPrice : selectPrice,
          brand: selectBrand ? selectBrand : brand,
        }
      } else if (Object.keys(selects[select.code]).length < Object.keys(select).length) {
        selects[select.code] = {
          ...selects[select.code],
          ...select,
          maxFavoriteAdded:
            favoriteAdded > selects[select.code].maxFavoriteAdded
              ? favoriteAdded
              : selects[select.code].maxFavoriteAdded,
          firstPrice: !selectPrice || firstPrice < selectPrice ? firstPrice : selectPrice,
          brand: selectBrand ? selectBrand : brand,
        }
      }

      if (!has(selects[select.code], 'variants')) {
        selects[select.code].variants = []
      }

      if (!isSelect) {
        // console.log(`push variant ${favorite.code} to ${select.code}`)
        selects[select.code].variants.push({ ...favorite, isFavorite: true })

        selects[select.code].maxFavoriteAdded =
          favoriteAdded > selects[select.code].maxFavoriteAdded
            ? favoriteAdded
            : selects[select.code].maxFavoriteAdded

        selects[select.code].firstPrice =
          !selects[select.code].firstPrice || firstPrice < selects[select.code].firstPrice
            ? firstPrice
            : selects[select.code].firstPrice

        selects[select.code].brand = selects[select.code].brand ? selects[select.code].brand : brand
      }
      return selects
    }, {})

    // const variants = favorites
    //   .filter(({ productType }) => productType !== 'select')
    //   .reduce((acc, favorite) => {
    //     const { parentId } = favorite
    //     if (!has(acc, parentId)) {
    //       acc[parentId] = []
    //     }
    //     acc[parentId].push(favorite)
    //     return acc
    //   }, {})

    return {
      ...productsData,
      data: Object.values(selects).sort((a, b) => {
        return compareDesc(a.maxFavoriteAdded, b.maxFavoriteAdded)
      }),
    }
  },
  [GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_FAVORITE_DATA_SORTED]: (state, getters) => {
    const sortKey = state[CONSTANTS.FAVORITES_SORT_AND_FILTER].sortKey
    const activeFilters = compact(Object.values(state[CONSTANTS.FAVORITES_SORT_AND_FILTER].filter))
    const filterIds = activeFilters.length
      ? intersection(...Object.values(activeFilters).map(filter => filter.value))
      : undefined

    const selects = (
      filterIds
        ? getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_FAVORITE_DATA].data.filter(select =>
            filterIds.includes(select.code),
          )
        : [...getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_FAVORITE_DATA].data]
    ).map(select => {
      const variants = filterIds
        ? select.variants.filter(variant => filterIds.includes(variant.code))
        : select.variants
      return {
        ...select,
        variants:
          state[CONSTANTS.FAVORITES_SORT_AND_FILTER].sortKey === defaultFavoritesSortKey
            ? variants
            : favoritesSortItemsBySortKey(variants, sortKey),
      }
    })

    return state[CONSTANTS.FAVORITES_SORT_AND_FILTER].sortKey === defaultFavoritesSortKey
      ? selects
      : favoritesSortItemsBySortKey(selects, sortKey)
  },
  [GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_FAVORITE_DATA_SORTED_COUNT]: (state, getters) => {
    return [
      ...getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_FAVORITE_DATA_SORTED],
      ...flatten(
        getters[GETTER_TYPES.FAVORITEPRODUCTS_DATA_WITH_FAVORITE_DATA_SORTED].map(
          select => select.variants,
        ),
      ),
    ].filter(item => item.isFavorite).length
  },
  [GETTER_TYPES.CUSTOMER_PROMISE]: state => get(state, MUTATION_TYPES.CUSTOMER.promiseKey),
  [GETTER_TYPES.CUSTOMER_STATE]: state => get(state, MUTATION_TYPES.CUSTOMER.stateKey),
})
